import { useEffect, useRef, useState } from 'react';
import { frameAt } from './preferences';

export const useEagleCanvas = ({ canvasRef, running, speed, retryKey, animation }) => {
  const [load, setLoad] = useState({ atlas: null, status: 'loading' });
  const clock = useRef(0);
  const settings = useRef({ running, speed });
  settings.current = { running, speed };
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const setStatus = status => setLoad({ atlas: animation.atlas, status });
    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) { setStatus('error'); return; }
    let active = true, raf = 0, previousTime = null, previousFrame = -1, onScreen = true, loaded = false;
    const image = new Image();
    image.decoding = 'async';
    clock.current = 0;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    delete canvas.dataset.frame;
    canvas.dataset.atlas = animation.atlas;
    setStatus('loading');
    const draw = frame => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const width = Math.max(1, Math.round(canvas.clientWidth * dpr));
      const height = Math.max(1, Math.round(canvas.clientHeight * dpr));
      if (canvas.width !== width || canvas.height !== height) { canvas.width = width; canvas.height = height; }
      ctx.clearRect(0, 0, width, height);
      const scale = Math.min(width / animation.width, height / animation.height);
      const dw = animation.width * scale, dh = animation.height * scale;
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(image, (frame % animation.columns) * animation.width, Math.floor(frame / animation.columns) * animation.height,
        animation.width, animation.height, (width - dw) / 2, (height - dh) / 2, dw, dh);
      canvas.dataset.frame = String(frame);
      previousFrame = frame;
    };
    const tick = now => {
      raf = 0;
      if (!active || !loaded || document.hidden || !onScreen) { previousTime = null; return; }
      if (settings.current.running && previousTime !== null) clock.current += Math.min(now - previousTime, 100) * settings.current.speed;
      previousTime = now;
      const frame = frameAt(clock.current, animation.durationMs, animation.frames);
      if (frame !== previousFrame) draw(frame);
      if (settings.current.running) raf = requestAnimationFrame(tick);
    };
    const refresh = () => {
      cancelAnimationFrame(raf); raf = 0; previousTime = null;
      if (loaded && active) {
        draw(frameAt(clock.current, animation.durationMs, animation.frames));
        if (settings.current.running && !document.hidden && onScreen) raf = requestAnimationFrame(tick);
      }
    };
    const resize = new ResizeObserver(refresh);
    resize.observe(canvas);
    const observer = typeof IntersectionObserver === 'undefined' ? null : new IntersectionObserver(entries => {
      onScreen = entries[0]?.isIntersecting !== false; refresh();
    }, { threshold: 0.01 });
    observer?.observe(canvas);
    document.addEventListener('visibilitychange', refresh);
    canvas.addEventListener('eagle-settings-change', refresh);
    const timeout = setTimeout(() => { if (active && !loaded) setStatus('error'); }, 12000);
    image.onload = () => { clearTimeout(timeout); if (active) { loaded = true; setStatus('ready'); refresh(); } };
    image.onerror = () => { clearTimeout(timeout); if (active) setStatus('error'); };
    image.src = animation.atlas;
    return () => {
      active = false; clearTimeout(timeout); cancelAnimationFrame(raf); resize.disconnect(); observer?.disconnect();
      document.removeEventListener('visibilitychange', refresh); canvas.removeEventListener('eagle-settings-change', refresh);
      image.onload = null; image.onerror = null;
    };
  }, [canvasRef, retryKey, animation]);
  useEffect(() => { canvasRef.current?.dispatchEvent(new Event('eagle-settings-change')); }, [canvasRef, running, speed]);
  return load.atlas === animation.atlas ? load.status : 'loading';
};